import { AcademicTitle } from '../../enums/academic-title.enum';
import { ApplicantSkillLevel } from '../../enums/applicant-skill-level.enum';
import { LanguageLevelCode } from '../../enums/language-level-code.enum';
import { EmploymentType } from '../../enums/employment-type.enum';
import { Gender } from '../../enums/gender.enum';
import { WorkingModel } from '../../enums/working-model.enum';
import { WorkingTime } from '../../enums/working-time.enum';
import { PlatformApplicationImportSource } from '../enums/platform-application-import-source.enum';
import { PlatformApplicationSource } from '../enums/platform-application-source.enum';
import { PlatformApplicationStatus } from '../enums/platform-application-status.enum';
import { PlatformAvailability } from '../enums/platform-availability.enum';
import { PlatformDocumentKind } from '../enums/platform-document-kind.enum';
import { PlatformImportedCvAction } from '../enums/platform-imported-cv-action.enum';
import { PlatformInvitationType } from '../enums/platform-invitation-type.enum';
import { PlatformMissingDataType } from '../enums/platform-missing-data-type.enum';
import { PlatformPauseDuration } from '../enums/platform-pause-duration.enum';
import { PlatformRecommendationMode } from '../enums/platform-recommendation-mode.enum';
import { PlatformTalenthubStatus } from '../enums/platform-talenthub-status.enum';

export const PLATFORM_DASHBOARD_PREVIEW_LIMIT = 4;

export const PLATFORM_EMPTY_USER: PlatformUser = {
  firstName: '',
  lastName: '',
  email: '',
  hasCv: false,
};

export interface PlatformUser {
  firstName: string;
  lastName: string;
  email: string;
  hasCv: boolean;
}

export interface PlatformActionItem {
  id: number;
  applicationId: number;
  jobTitle: string;
  companyId: number | null;
  companyName: string;
  missingData: PlatformMissingDataType[];
}

export interface PlatformInvitation {
  id: number;
  jobId: number;
  companyId: number;
  type: PlatformInvitationType;
  companyName: string;
  jobTitle: string;
  location: string;
  salary: string;
  receivedAt: string;
  closed: boolean;
  jobUrl: string | null;
  score: number | null;
}

export interface PlatformApplication {
  id: number;
  jobTitle: string;
  companyId: number | null;
  companyName: string;
  location: string;
  appliedAt: string;
  status: PlatformApplicationStatus;
  missingData: PlatformMissingDataType[];
  jobAvailable: boolean;
  source: PlatformApplicationSource;
  jobUrl: string | null;
}

export interface PlatformSavedJob {
  id: number;
  jobId: number;
  companyId: number;
  jobTitle: string;
  companyName: string;
  location: string;
  salary: string;
  pinnedAt: string;
  updatedAt: string;
  applied: boolean;
  closed: boolean;
  jobUrl: string | null;
}

export interface PlatformBoardJob {
  id: number;
  companyId: number;
  jobTitle: string;
  companyName: string;
  location: string;
  salary: string;
  salaryMin: number | null;
  salaryMax: number | null;
  workingModel: WorkingModel | null;
  employmentType: EmploymentType | null;
  workingTime: WorkingTime | null;
  postedAt: string;
  updatedAt: string;
  recommended: boolean;
  invited: boolean;
  score: number | null;
  listed: boolean;
  open: boolean;
  skills: string[];
}

/** Compact job card as returned by the candidate platform API. */
export interface PlatformApiJob {
  id: number;
  companyId: number;
  jobTitle: string;
  companyName: string;
  location: string;
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string | null;
  salaryInterval: string | null;
  workingModel: WorkingModel | null;
  employmentType: EmploymentType | null;
  workingTime: WorkingTime | null;
  updatedAt: string;
  availability: PlatformApiJobAvailability;
  listed: boolean;
  jobUrl: string;
  skills: string[];
}

export enum PlatformApiJobAvailability {
  OPEN = 'open',
  CLOSED = 'closed',
  GONE = 'gone',
}

export interface PlatformApiSavedJob {
  job: PlatformApiJob;
  savedAt: string;
  applied: boolean;
}

export interface PlatformApiInvitation {
  job: PlatformApiJob;
  receivedAt: string;
  score: number | null;
}

export interface PlatformApiRecommendation {
  job: PlatformApiJob;
  score: number | null;
  invited: boolean;
  receivedAt: string;
}

export interface PlatformImportableApplication {
  id: number;
  jobTitle: string;
  companyName: string;
  appliedAt: string;
  source: PlatformApplicationImportSource;
}

export interface PlatformExtractedProfile {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  city: string | null;
  currentOccupation: string | null;
  skills: string[];
}

export interface PlatformProfileSkill {
  name: string;
  level: ApplicantSkillLevel;
}

export interface PlatformProfileLanguage {
  code: string;
  level: LanguageLevelCode;
}

export interface PlatformDocumentApplicationUsage {
  id: number;
  jobTitle: string;
  companyName: string;
}

export interface PlatformDocument {
  id: number;
  name: string;
  kind: PlatformDocumentKind;
  usedInApplications: PlatformDocumentApplicationUsage[];
}

export interface PlatformImportedCv {
  id: number;
  name: string;
  applicationId: number;
  action: PlatformImportedCvAction;
}

export interface PlatformProfile {
  firstName: string;
  lastName: string;
  title: Gender;
  academicTitle: AcademicTitle | null;
  currentOccupation: string;
  email: string;
  phone: string;
  city: string;
  photoSrc: string | null;
  preferredLocations: string[];
  workingModel: WorkingModel | null;
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string;
  availability: PlatformAvailability | null;
  linkedin: string;
  xing: string;
  documents: PlatformDocument[];
  pendingImportedCvs: PlatformImportedCv[];
  skills: PlatformProfileSkill[];
  languages: PlatformProfileLanguage[];
  documentParsingsRemaining: number;
  startDate: number | null;
  isaCompleted: boolean;
}

/** Profile as returned by GET /candidate/platform/me. */
export interface PlatformApiProfile {
  firstName: string;
  lastName: string;
  title: Gender;
  academicTitle: AcademicTitle | null;
  email: string;
  currentOccupation: string | null;
  phone: string | null;
  city: string | null;
  photoUrl: string | null;
  preferredLocations: string[];
  workingModel: WorkingModel | null;
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string | null;
  availability: PlatformAvailability | null;
  /** Earliest start date as ISO calendar day, derived from availability when set. */
  startDate?: string | null;
  linkedin: string | null;
  xing: string | null;
  documents: {
    id: number;
    name: string;
    type: string;
    main: boolean;
    createdAt: string;
    usedInApplications?: PlatformDocumentApplicationUsage[];
  }[];
  skills: PlatformProfileSkill[];
  languages: PlatformProfileLanguage[];
  documentParsingsRemaining: number;
  isaCompleted: boolean;
}

export interface PlatformMe {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  hasPassword: boolean;
  usedSso: boolean;
  onboardingCompleted: boolean;
  talenthubStatus: PlatformTalenthubStatus;
  termsAcceptedAt: string | null;
  recommendationPausedUntil: string | null;
  recommendationsPaused: boolean;
  notifyTalentpoolInvitation: boolean;
  notifyJobRecommendation: boolean;
  notifyApplicationStatus: boolean;
  /** Whether a default CV exists — full documents live on GET /profile. */
  hasMainCv: boolean;
}

export interface PlatformProfileUpdate {
  firstName?: string;
  lastName?: string;
  title?: Gender;
  academicTitle?: AcademicTitle | null;
  currentOccupation?: string | null;
  phone?: string | null;
  city?: string | null;
  preferredLocations?: string[];
  workingModel?: WorkingModel | null;
  salaryMin?: number | null;
  salaryMax?: number | null;
  currency?: string | null;
  availability?: PlatformAvailability | null;
  linkedin?: string | null;
  xing?: string | null;
  skills?: PlatformProfileSkill[];
  languages?: PlatformProfileLanguage[];
}

export interface PlatformJobRequirements {
  phone: boolean;
  startDate: boolean;
  salary: boolean;
  cv: boolean;
  coverLetter: boolean;
  certificates: boolean;
}

export interface PlatformApplyWithProfilePayload {
  firstName?: string;
  lastName?: string;
  phone?: string;
  city?: string;
  salary?: number | null;
  startDate?: number | null;
  skills: PlatformProfileSkill[];
  certificateNames?: string[];
  cvFile?: File | null;
  coverLetterFile?: File | null;
  certificateFiles?: File[];
}

export interface PlatformFollowUpPayload {
  salary?: number | null;
  startDate?: number | null;
  skills?: PlatformProfileSkill[];
  documents?: PlatformDocument[];
  isaCompleted?: boolean;
  cvFile?: File | null;
  certificateFiles?: File[];
  isaAnswers?: number[];
}

export const PLATFORM_DEFAULT_REQUIREMENTS: PlatformJobRequirements = {
  phone: false,
  startDate: true,
  salary: false,
  cv: true,
  coverLetter: false,
  certificates: false,
};

/**
 * Merges job-specific requirements onto the default set.
 * @param override - Optional per-job required fields
 * @returns Resolved required fields
 */
export function resolveJobRequirements(
  override?: Partial<PlatformJobRequirements> | null
): PlatformJobRequirements {
  return {
    ...PLATFORM_DEFAULT_REQUIREMENTS,
    ...override,
  };
}

export interface PlatformSettings {
  recommendationMode: PlatformRecommendationMode;
  pauseDuration: PlatformPauseDuration;
  pausedUntil: string | null;
  pausedAt: string | null;
  notifyTalentpoolInvitation: boolean;
  notifyJobRecommendation: boolean;
  notifyApplicationStatus: boolean;
}

export const PLATFORM_DEFAULT_SETTINGS: PlatformSettings = {
  recommendationMode: PlatformRecommendationMode.RECEIVE,
  pauseDuration: PlatformPauseDuration.SIX_MONTHS,
  pausedUntil: null,
  pausedAt: null,
  notifyTalentpoolInvitation: true,
  notifyJobRecommendation: true,
  notifyApplicationStatus: true,
};

export const PLATFORM_PROFILE_CURRENCIES: readonly string[] = ['EUR', 'USD', 'GBP', 'CHF'];

export const PLATFORM_PROFILE_SKILL_LEVELS: ApplicantSkillLevel[] = [
  ApplicantSkillLevel.EXISTING,
  ApplicantSkillLevel.FUNDAMENTAL,
  ApplicantSkillLevel.FUNDAMENTAL_PLUS,
  ApplicantSkillLevel.GOOD,
  ApplicantSkillLevel.VERY_GOOD,
  ApplicantSkillLevel.EXPERT,
];

export const PLATFORM_PROFILE_LANGUAGE_LEVELS: LanguageLevelCode[] = [
  LanguageLevelCode.NL,
  LanguageLevelCode.C2,
  LanguageLevelCode.C1,
  LanguageLevelCode.B2,
  LanguageLevelCode.B1,
  LanguageLevelCode.A2,
  LanguageLevelCode.A1,
  LanguageLevelCode.NA,
];

export const PLATFORM_PROFILE_WORKING_MODELS: WorkingModel[] = [
  WorkingModel.ON_SITE,
  WorkingModel.HYBRID,
  WorkingModel.REMOTE,
];

export const PLATFORM_PROFILE_CITY_SUGGESTIONS: string[] = [
  'Berlin',
  'Hamburg',
  'München',
  'Köln',
  'Frankfurt am Main',
  'Stuttgart',
  'Düsseldorf',
  'Leipzig',
  'Dortmund',
  'Essen',
  'Bremen',
  'Dresden',
  'Hannover',
  'Nürnberg',
  'Baden-Württemberg',
  'Bayern',
  'Brandenburg',
  'Hessen',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thüringen',
  'Mecklenburg-Vorpommern',
];

/**
 * Formats a salary range for job cards.
 * @param salaryMin - Minimum salary
 * @param salaryMax - Maximum salary
 * @param currency - Currency code
 * @returns Display string or empty
 */
export function formatPlatformSalary(
  salaryMin?: number | null,
  salaryMax?: number | null,
  currency?: string | null
): string {
  const suffix = currency === 'EUR' || !currency ? '€' : currency;
  if (salaryMin && salaryMax) {
    return `${salaryMin.toLocaleString('de-DE')} – ${salaryMax.toLocaleString('de-DE')} ${suffix}`;
  }
  if (salaryMin) {
    return `${salaryMin.toLocaleString('de-DE')} ${suffix}`;
  }
  if (salaryMax) {
    return `${salaryMax.toLocaleString('de-DE')} ${suffix}`;
  }
  return '';
}

/**
 * Maps the API job card to the board job used by the platform views.
 * @param job - API job
 * @param extra - Recommendation flags
 * @returns Board job
 */
export function toPlatformBoardJob(
  job: PlatformApiJob,
  extra: { recommended?: boolean; invited?: boolean; score?: number | null } = {}
): PlatformBoardJob {
  return {
    id: job.id,
    companyId: job.companyId,
    jobTitle: job.jobTitle,
    companyName: job.companyName,
    location: job.location,
    salary: formatPlatformSalary(job.salaryMin, job.salaryMax, job.currency),
    salaryMin: job.salaryMin,
    salaryMax: job.salaryMax,
    workingModel: job.workingModel,
    employmentType: job.employmentType,
    workingTime: job.workingTime,
    postedAt: job.updatedAt,
    updatedAt: job.updatedAt,
    recommended: extra.recommended === true,
    invited: extra.invited === true,
    score: extra.score ?? null,
    listed: job.listed,
    open: job.availability === PlatformApiJobAvailability.OPEN,
    skills: job.skills,
  };
}
